import React, { useEffect, useRef } from 'react';
import { Box, IconButton, Typography, useTheme } from '@mui/material';
import {
  PlayArrow,
  Pause,
  Refresh,
  SkipNext,
} from '@mui/icons-material';
import { useReducedMotionPreferred } from '../../hooks/useReducedMotionPreferred';
import AnimatedNumber from '../shared/AnimatedNumber';

// SVG circle dimensions
const RADIUS = 110;
const STROKE_WIDTH = 12;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const SVG_SIZE = (RADIUS + STROKE_WIDTH) * 2;
const CENTER = RADIUS + STROKE_WIDTH;

function formatMmSs(ms) {
  const totalSecs = Math.ceil(ms / 1000);
  const m = Math.floor(totalSecs / 60);
  const s = totalSecs % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function getModeColor(mode, theme) {
  if (mode === 'work') return theme.palette.primary.main;
  if (mode === 'shortBreak') return theme.palette.info.main;
  return theme.palette.success.main;
}

function getModeLabel(mode) {
  if (mode === 'work') return 'Work';
  if (mode === 'shortBreak') return 'Short Break';
  return 'Long Break';
}

/**
 * PomodoroTimer
 * SVG dial-based timer display. Consumes state and controls from usePomodoro.
 *
 * @param {{
 *   mode: 'work'|'shortBreak'|'longBreak',
 *   remainingMs: number,
 *   durationMs: number,
 *   isRunning: boolean,
 *   cycleCount: number,
 *   start: () => void,
 *   pause: () => void,
 *   reset: () => void,
 *   skip: () => void,
 * }} props
 */
function PomodoroTimer({ mode, remainingMs, durationMs, isRunning, cycleCount, start, pause, reset, skip }) {
  const theme = useTheme();
  const reducedMotion = useReducedMotionPreferred();
  const ringRef = useRef(null);
  const prevRemainingRef = useRef(remainingMs);

  const ringColor = getModeColor(mode, theme);
  const progress = durationMs > 0 ? remainingMs / durationMs : 1;
  const dashOffset = CIRCUMFERENCE * (1 - progress);

  // Animate stroke-dashoffset via direct DOM mutation to avoid React re-render per frame.
  // When reduced-motion is preferred, transition is removed and we set the offset directly.
  useEffect(() => {
    const el = ringRef.current;
    if (!el) return;

    if (reducedMotion) {
      el.style.transition = 'none';
      el.style.strokeDashoffset = dashOffset;
    } else {
      // When the timer starts running or mode changes, apply a smooth CSS transition.
      // The duration matches time remaining so the ring drains linearly.
      if (isRunning && prevRemainingRef.current > remainingMs) {
        el.style.transition = `stroke-dashoffset 0.3s linear`;
      } else {
        // Reset/skip — snap without transition
        el.style.transition = 'stroke-dashoffset 0.15s ease';
      }
      el.style.strokeDashoffset = dashOffset;
    }
    prevRemainingRef.current = remainingMs;
  }, [dashOffset, isRunning, reducedMotion, remainingMs]);

  const handlePlayPause = () => {
    if (isRunning) pause();
    else start();
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
      {/* Mode label */}
      <Typography
        variant="overline"
        sx={{ color: ringColor, letterSpacing: 2, fontWeight: 700, opacity: 0.9 }}
      >
        {getModeLabel(mode)}
      </Typography>

      {/* SVG Dial */}
      <Box
        role="img"
        aria-label={`Pomodoro timer, ${formatMmSs(remainingMs)} remaining`}
        sx={{ position: 'relative', display: 'inline-flex' }}
      >
        <svg
          width={SVG_SIZE}
          height={SVG_SIZE}
          style={{ transform: 'rotate(-90deg)' }}
        >
          {/* Track */}
          <circle
            cx={CENTER}
            cy={CENTER}
            r={RADIUS}
            fill="none"
            stroke={theme.palette.divider}
            strokeWidth={STROKE_WIDTH}
          />
          {/* Progress ring */}
          <circle
            ref={ringRef}
            cx={CENTER}
            cy={CENTER}
            r={RADIUS}
            fill="none"
            stroke={ringColor}
            strokeWidth={STROKE_WIDTH}
            strokeLinecap="round"
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={dashOffset}
            style={{ willChange: 'stroke-dashoffset' }}
          />
        </svg>

        {/* Center display */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography
            variant="h3"
            fontWeight={800}
            sx={{
              fontVariantNumeric: 'tabular-nums',
              letterSpacing: 1,
              color: 'text.primary',
            }}
          >
            <AnimatedNumber
              value={Math.ceil(remainingMs / 1000)}
              format={formatMmSs}
              duration={reducedMotion ? 0 : 0.3}
            />
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            {cycleCount > 0 ? `${cycleCount} done` : 'Ready'}
          </Typography>
        </Box>
      </Box>

      {/* Controls */}
      <Box display="flex" gap={2} alignItems="center">
        {/* Reset */}
        <IconButton
          onClick={reset}
          aria-label="Reset timer"
          sx={{
            width: 44,
            height: 44,
            bgcolor: 'action.hover',
            color: 'text.secondary',
            '&:hover': { bgcolor: 'action.selected' },
          }}
        >
          <Refresh />
        </IconButton>

        {/* Play / Pause — primary action */}
        <IconButton
          onClick={handlePlayPause}
          aria-label={isRunning ? 'Pause timer' : 'Start timer'}
          sx={{
            width: 64,
            height: 64,
            bgcolor: ringColor,
            color: '#fff',
            boxShadow: theme.shadowsExt?.md,
            '&:hover': { bgcolor: ringColor, opacity: 0.9 },
          }}
        >
          {isRunning ? <Pause fontSize="large" /> : <PlayArrow fontSize="large" />}
        </IconButton>

        {/* Skip */}
        <IconButton
          onClick={skip}
          aria-label="Skip to next block"
          sx={{
            width: 44,
            height: 44,
            bgcolor: 'action.hover',
            color: 'text.secondary',
            '&:hover': { bgcolor: 'action.selected' },
          }}
        >
          <SkipNext />
        </IconButton>
      </Box>
    </Box>
  );
}

export default PomodoroTimer;
